// react
import React, { useEffect, useState } from "react";
// ui component
import { Button, Dialog, Select } from "../../../components/UI";
// constants (icons)
import {
  CapacityIcon,
  DimesionsIcon,
  GetDetailsIcon,
} from "../../../constants/icons";
import AutoComplete from "../../../../Components/AutoComplete";
import {
  getIntraCityBookingPriceEstimated,
  getIntraCityRates,
} from "../../../../services/intraCityRate";
import { showHttpError } from "../../../../utils/message";

// type 
type IntraCityRateEstimateItemType = {
  name: string;
  maxCapacity: number;
  perMinuteCharge: number;
  price: number;
}

// FC: Intra City Get Estimate Component
export default function IntraCityForm() {
  const [cities, setCities] = useState<any>(null);
  const [selectedCity, setSelectedCity] = useState<any>(null);
  const [sourceLocation, setSourceLocation] = useState<any>(null);
  const [destinationLocation, setDestinationLocation] = useState<any>(null);
  const [isIntraCityRateDialogOpen, setIsIntraCityRateDialogOpen] =
    useState<boolean>(() => false);
  const [
    intraCityBookingPriceEstimateArray,
    setIntraCityBookingPriceEstimateArray,
  ] = useState<IntraCityRateEstimateItemType[]>(() => []);
  const [selectedEstimatedRate, setSelectedEstimatedRate] = useState<number>();

  // find cities options
  const citiesOption = Array.from(new Set(cities?.map((e: any) => e.name))).map(
    (name) => {
      const city = cities.find((e: any) => e.name === name);
      return {
        label: city.name,
        value: city._id,
      };
    }
  );

  // does: get the city list
  async function getCityList() {
    try {
      let payload = { type: "EXPRESS" };
      const responseForCity = await getIntraCityRates(payload);
      // Using a Map to ensure uniqueness based on _id
      const cityMap = new Map();
      responseForCity.forEach((item: any) => {
        // Skip the item if city is undefined
        if (item.city) {
          const city = item.city;
          if (!cityMap.has(city._id)) {
            cityMap.set(city._id, { _id: city._id, name: city.name });
          }
        }
      });
      // Converting the Map values to an array
      const uniqueCities = Array.from(cityMap.values());
      setCities(uniqueCities);
    } catch (error) {
      showHttpError(error);
    }
  }

  // does: handle submit
  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    try {
      if (!selectedCity || !sourceLocation || !destinationLocation) {
        return alert("All details are required!");
      }
      const payload = {
        city: selectedCity.label,
        toPlace: destinationLocation.place_id,
        fromPlace: sourceLocation.place_id,
      };
      const response = await getIntraCityBookingPriceEstimated(payload);
      const data: IntraCityRateEstimateItemType[] = response.price.map((item: any) => {
        return {
          name: item.vehicleTypes?.name,
          maxCapacity: item.vehicleTypes?.maxCapacity,
          price: item.price,
          perMinuteCharge: item.perMinuteCharge,
        };
      });
      setIntraCityBookingPriceEstimateArray(data);
      if (response) {
        setIsIntraCityRateDialogOpen(true);
      }
    } catch (error) {
      alert("Intra city booking rate error");
    }
  }

  // does: handle click on the dailog
  function handleClick(index: number) {
    setSelectedEstimatedRate(index);
  }

  // useEffect
  // does: get the city list and vechile type list
  useEffect(() => {
    getCityList();
  }, []);

  // component return
  return (
    <form className="| flex-col gap-1" onSubmit={handleSubmit}>
      <Select
        isClearable={true}
        isSearchable={true}
        options={citiesOption}
        value={selectedCity}
        onChange={(e: any) => setSelectedCity(e)}
        placeholder={"Select Your City"}
      />
      {/* <Select isClearable={true} isSearchable={true} options={options} placeholder={"Source Location"} /> */}
      <AutoComplete
        placeHolder={"Source Location"}
        value={sourceLocation && sourceLocation.name}
        onChange={(e: any) => {
          setSourceLocation({ name: e.name, place_id: e.place_id });
        }}
      ></AutoComplete>
      <AutoComplete
        value={destinationLocation && destinationLocation.name}
        placeHolder={"Destination Location"}
        onChange={(e: any) => {
          setDestinationLocation({ name: e.name, place_id: e.place_id });
        }}
      ></AutoComplete>
      {/* <Select isClearable={true} isSearchable={true} options={options} placeholder={"Destination Location"} /> */}
      <Button
        variant="secondary"
        action="secondary"
        Icon={<GetDetailsIcon />}
        className="as-start fs-button"
      >
        Book Now
      </Button>

      {/* Dialog for display Intra City Rates */}
      <Dialog
        header={"Intra City Rates"}
        body={
          <div className="grid gap-1">
            {intraCityBookingPriceEstimateArray &&
              intraCityBookingPriceEstimateArray.map((item, index: number) => {
                return (
                  <div key={index} className={`intra-city-rate-estimated-item | paper fs-link grid gap-4 pointer ${selectedEstimatedRate === index ? "active" : ""}`} onClick={() => handleClick(index)}>
                    <div className="flex-between flex-wrap">
                      <span className="fw-600">{item.name || "Vehicle"}</span>
                      <strong style={{fontSize: "clamp(18px, 4vw, 1.5rem)"}} className="px-1 py-4 oval bg-success-50 text-success-900 shadow-small">₹{item.price}</strong>
                    </div>
                    <div className="flex flex-wrap ai-center gap-8">
                    <p>
                        <CapacityIcon /> {item.maxCapacity}kg
                    </p>
                      {
                        <p>
                          <DimesionsIcon /> 40<span>L</span>x50<span>B</span>x
                          <span>10</span>
                        </p>
                      }
                    </div>
                    <p className="per-min-charge-text text-black-100">
                      First 40 min free, then{" "}
                      <span className="per-min-charge text-primary-400">
                        {item.perMinuteCharge}₹/min
                      </span>{" "} is applicable
                    </p>
                  </div>
                );
              })}
          </div>
        }
        footer={
          <div className="flex gap-1">
            <Button variant="primary" action="secondary" type="button">
              Book
            </Button>
          </div>
        }
        size="regular"
        state={isIntraCityRateDialogOpen}
        setState={setIsIntraCityRateDialogOpen}
        closeIcon
      ></Dialog>
    </form>
  );
}
